import React from 'react';
import { applySpelling } from '@/utils/spelling';
import { useRegion } from '@/contexts/RegionContext';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Globe2, ArrowRight, Target, Lightbulb, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRegionalPageContent } from '@/hooks/useRegionalPageContent';
import { LazyImage } from '@/components/ui/LazyImage';

export function AboutPage() {
  const { regionCode } = useRegion();
  const s = (text: string) => applySpelling(text, regionCode);
  const { t } = useTranslation();
  const { content: regionalContent } = useRegionalPageContent('about');

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: t('nav.about', 'About Us') }
        ]} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-3xl">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-[1px] w-8 bg-rule-3"></span>
              <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">
                Our Story
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight mb-8 text-ink font-normal">
              {regionalContent?.title || 'Redefining human potential through data and depth.'}
            </h1>
            
            {regionalContent?.htmlContent ? (
              <div 
                className="prose prose-brand max-w-none prose-p:text-xl prose-p:font-light prose-p:text-ink/70/80 prose-p:mb-12 prose-p:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: regionalContent.htmlContent }}
              />
            ) : (
              <p className="text-xl font-light text-ink/70/80 mb-12 leading-relaxed">
                We founded FIVE LENS with a singular mission: to move beyond the limitations of single-lens personality typing and provide a holistic, scientifically rigorous framework for human development.
              </p>
            )}
          </div>

          <div className="mb-24 w-full h-[500px] overflow-hidden bg-ground-deep relative">
            <div className="absolute inset-0 bg-ink/20 mix-blend-multiply z-10" />
            <LazyImage 
              src="/five_lens_about_our_story_web.webp"
              alt="FIVE LENS Enneagram team collaborating on people development research and methodology"
              className="w-full h-full object-cover relative z-0"
              referrerPolicy="no-referrer"
            />
          </div>

          <div className="grid lg:grid-cols-12 gap-16 mb-24">
            <div className="lg:col-span-4">
              <h2 className="text-3xl font-serif text-ink mb-6 leading-tight">
                A Decade of Research
              </h2>
              <div className="h-1 w-12 bg-rule-3 mb-6"></div>
            </div>
            <div className="lg:col-span-8 prose prose-lg prose-brand max-w-none text-ink/70/80 leading-relaxed font-light">
              <p className="mb-6">
                {s("The FIVE LENS framework is the culmination of over ten years of rigorous academic research, statistical validation, and real-world corporate application. We recognized early on that human behavior cannot be neatly categorized into four quadrants or single archetypes.")}
              </p>
              <p>
                {s("By synthesizing the Enneagram with proven models of Personal Mastery, Emotional Resilience, Social Drives, and Energy Centres, we developed a multidimensional tool that measures not just who a person is, but how they adapt, grow, and respond to systemic pressure. Today, our framework is utilized by leading enterprises and certified practitioners worldwide.")}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-24">
            <div className="bg-white p-10 border border-ground-deep hover:border-rule-3 transition-colors group">
              <Target className="w-10 h-10 text-rule-3 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="text-xl font-serif font-bold text-ink mb-4">Our Mission</h3>
              <p className="text-ink/70/70 leading-relaxed font-light mb-6">
                {s("To equip leaders, coaches, and organizations with the unprecedented behavioral insight required to build high-trust, resilient, and high-performing cultures.")}
              </p>
            </div>
            
            <div className="bg-white p-10 border border-ground-deep hover:border-rule-3 transition-colors group transform md:-translate-y-4">
              <ShieldCheck className="w-10 h-10 text-rule-3 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="text-xl font-serif font-bold text-ink mb-4">Scientific Rigor</h3>
              <p className="text-ink/70/70 leading-relaxed font-light mb-6">
                We are committed to maintaining the highest standards of construct validity and reliability, ensuring our assessments deliver measurable ROI for enterprise clients.
              </p>
            </div>
            
            <div className="bg-white p-10 border border-ground-deep hover:border-rule-3 transition-colors group">
              <Globe2 className="w-10 h-10 text-rule-3 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="text-xl font-serif font-bold text-ink mb-4">Global Reach</h3>
              <p className="text-ink/70/70 leading-relaxed font-light mb-6">
                With a thriving community of accredited practitioners across the globe, we deliver localized, culturally-nuanced interventions in multiple languages.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-ink via-ink/80 to-ink/90 text-ground p-10 md:p-16 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-serif mb-4 text-white">Join our community</h2>
              <p className="text-white text-lg mb-8 max-w-xl font-light">
                {s("Whether you\'re looking to transform your organization\'s culture or deepen your own coaching practice, we invite you to explore the power of the FIVE LENS framework.")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/framework"
                  className="inline-flex items-center justify-center bg-rule-3 text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-white hover:text-ink transition-colors gap-2"
                >
                  Explore the Methodology <ArrowRight className="w-4 h-4" />
                </Link>
                <Link 
                  to="/contact"
                  className="inline-flex items-center justify-center border border-white text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-white/10 transition-colors"
                >
                  Contact Us
                </Link>
              </div>
            </div>
            <Lightbulb className="absolute -bottom-8 -right-8 w-80 h-80 text-white/5 pointer-events-none" />
          </div>
        </motion.div>
      </div>
    </main>
  );
}
